import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  ArrowRight,
  Clock,
  CheckCircle,
  Users,
  Briefcase,
  Scale,
  Languages,
  Shield,
  Laptop,
  Home,
  Package,
  HelpCircle,
} from "lucide-react";
import { useBooking } from "@/contexts/BookingContext";
import ChapterIndicator from "./ChapterIndicator";
import { useI18n } from "@/contexts/I18nContext";

const CinematicFinalCTA = () => {
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const { openBooking } = useBooking();
  const { t } = useI18n();

  // Service definitions using translations
  const services = [
    { id: "gestoria", name: t.services.gestoria.name, icon: Briefcase },
    { id: "legal", name: t.services.legal.name, icon: Scale },
    { id: "idiomas", name: t.services.idiomas.name, icon: Languages },
    { id: "seguros", name: t.services.seguros.name, icon: Shield },
    { id: "digital", name: t.services.digital.name, icon: Laptop },
    { id: "relocation", name: t.services.relocation.name, icon: Package },
    { id: "viviendas", name: t.services.viviendas.name, icon: Home },
    { id: "otro", name: t.cta.serviceOther, icon: HelpCircle },
  ];

  const trustItems = [
    { icon: Clock, text: t.cta.trustResponse },
    { icon: CheckCircle, text: t.cta.trustFamilies },
    { icon: Users, text: t.cta.trustLanguage },
  ];


  const handleServiceSelect = useCallback((serviceId: string) => {
    setSelectedService(serviceId);
  }, []);

  const handleContinue = useCallback(() => {
    if (selectedService) {
      // Open the booking modal with preselected service
      openBooking(selectedService);
    }
  }, [selectedService, openBooking]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, serviceId: string) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleServiceSelect(serviceId);
      }
    },
    [handleServiceSelect],
  );

  return (
    <>
      {/* Scoped Styles for Contact Section */}
      <style>{`
        /* ===== ACTO VI - CONTACTO SECTION - COMPLETE REBUILD ===== */
        
        /* Main Section Container */
        .contact-section {
          position: relative;
          width: 100%;
          padding: 36px 14px;
          min-height: auto;
          overflow: hidden;
          box-sizing: border-box;
        }
        
        @media (min-width: 768px) {
          .contact-section {
            padding: 48px 20px;
          }
        }
        
        @media (min-width: 1024px) {
          .contact-section {
            padding: 60px 28px;
          }
        }
        
        /* Background Gradient */
        .contact-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #A81C1C 0%, #B91C1C 30%, #991B1B 70%, #7F1D1D 100%);
        }
        
        .contact-bg-overlay {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse at top center, rgba(255,255,255,0.12) 0%, transparent 60%);
        }
        
        .contact-bg-grid {
          position: absolute;
          inset: 0;
          opacity: 0.03;
          background-image: 
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 40px 40px;
        }
        
        /* Content Container */
        .contact-container {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 800px;
          margin: 0 auto;
          box-sizing: border-box;
        }
        
        /* Header Section */
        .contact-header {
          text-align: center;
          margin-bottom: 24px;
        }
        
        @media (min-width: 768px) {
          .contact-header {
            margin-bottom: 32px;
          }
        }
        
        /* Chapter Badge */
        .contact-chapter-badge {
          display: inline-flex;
          align-items: center;
          padding: 8px 16px;
          border-radius: 20px;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.3);
          margin-bottom: 12px;
          font-size: 13px;
          color: white;
          font-weight: 500;
        }
        
        @media (min-width: 1024px) {
          .contact-chapter-badge {
            padding: 10px 24px;
            font-size: 14px;
            margin-bottom: 16px;
          }
        }
        
        .contact-chapter-badge .pulse-dot {
          width: 8px;
          height: 8px;
          background: #4ade80;
          border-radius: 50%;
          margin-right: 10px;
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        @keyframes pulse-glow {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        
        /* Main Title */
        .contact-title {
          font-size: 24px;
          font-weight: 800;
          color: white;
          margin: 12px 0 6px;
          line-height: 1.15;
          font-family: 'Montserrat', sans-serif;
        }
        
        @media (min-width: 768px) {
          .contact-title {
            font-size: 30px;
            margin: 16px 0 8px;
          }
        }
        
        @media (min-width: 1024px) {
          .contact-title {
            font-size: 36px;
            margin: 20px 0 10px;
          }
        }
        
        .contact-title span {
          background: linear-gradient(90deg, white, rgba(255,255,255,0.85));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        /* Subtitle */
        .contact-subtitle {
          font-size: 14px;
          color: rgba(255,255,255,0.9);
          margin-bottom: 24px;
          line-height: 1.5;
        }
        
        @media (min-width: 1024px) {
          .contact-subtitle {
            font-size: 16px;
            margin-bottom: 32px;
          }
        }
        
        /* Form Card Container */
        .contact-form-card {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          border: 1px solid rgba(255,255,255,0.2);
          padding: 20px 16px;
          margin-bottom: 24px;
        }
        
        @media (min-width: 768px) {
          .contact-form-card {
            padding: 28px 24px;
            margin-bottom: 32px;
          }
        }
        
        @media (min-width: 1024px) {
          .contact-form-card {
            padding: 32px;
          }
        }
        
        /* ===== STEPPER ===== */
        .contact-stepper {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-bottom: 24px;
        }
        
        @media (min-width: 1024px) {
          .contact-stepper {
            gap: 12px;
            margin-bottom: 32px;
          }
        }
        
        .contact-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }
        
        .contact-step-circle {
          width: 32px;
          height: 32px;
          min-width: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 700;
          transition: all 0.3s ease;
        }
        
        @media (min-width: 1024px) {
          .contact-step-circle {
            width: 40px;
            height: 40px;
            min-width: 40px;
            font-size: 16px;
          }
        }
        
        .contact-step-circle.active {
          background: white;
          color: #B91C1C;
        }
        
        .contact-step-circle.inactive {
          background: transparent;
          border: 2px solid rgba(255,255,255,0.4);
          color: rgba(255,255,255,0.5);
        }
        
        .contact-step-label {
          font-size: 11px;
          color: rgba(255,255,255,0.7);
          text-align: center;
          max-width: 80px;
        }
        
        @media (min-width: 1024px) {
          .contact-step-label {
            font-size: 13px;
          }
        }
        
        /* Hide labels on mobile except active */
        @media (max-width: 639px) {
          .contact-step-label {
            display: none;
          }
          .contact-step.active .contact-step-label {
            display: block;
          }
        }
        
        .contact-step-arrow {
          width: 12px;
          height: 12px;
          color: rgba(255,255,255,0.5);
          flex-shrink: 0;
        }
        
        @media (min-width: 1024px) {
          .contact-step-arrow {
            width: 16px;
            height: 16px;
          }
        }
        
        /* Step Title */
        .contact-step-title {
          text-align: center;
          font-size: 15px;
          font-weight: 600;
          color: white;
          margin-bottom: 16px;
        }
        
        @media (min-width: 1024px) {
          .contact-step-title {
            font-size: 17px;
            margin-bottom: 20px;
          }
        }
        
        /* ===== SERVICE CARDS GRID ===== */
        .contact-services-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          width: 100%;
          margin-bottom: 16px;
        }
        
        @media (min-width: 768px) {
          .contact-services-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
        }
        
        @media (min-width: 1024px) {
          .contact-services-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 14px;
            margin-bottom: 20px;
          }
        }
        
        /* Individual Service Card */
        .contact-service-card {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          gap: 6px;
          padding: 12px 8px;
          min-height: 68px;
          background: rgba(255,255,255,0.12);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.25s ease;
          box-sizing: border-box;
          -webkit-tap-highlight-color: transparent;
        }
        
        @media (min-width: 1024px) {
          .contact-service-card {
            padding: 16px 12px;
            min-height: 85px;
          }
        }
        
        .contact-service-card:hover {
          background: rgba(255,255,255,0.18);
          border-color: rgba(255,255,255,0.4);
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.15);
        }
        
        .contact-service-card:focus {
          outline: 2px solid white;
          outline-offset: 2px;
        }
        
        .contact-service-card.selected {
          background: white;
          border: 2px solid white;
          transform: scale(1.02);
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
        }
        
        @media (max-width: 1023px) {
          .contact-service-card.selected {
            transform: scale(1);
          }
        }
        
        .contact-service-icon {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          color: white;
          transition: color 0.2s ease;
        }
        
        @media (min-width: 1024px) {
          .contact-service-icon {
            width: 28px;
            height: 28px;
          }
        }
        
        .contact-service-card.selected .contact-service-icon {
          color: #B91C1C;
        }
        
        .contact-service-name {
          font-size: 11px;
          font-weight: 600;
          color: white;
          line-height: 1.3;
          word-wrap: break-word;
          max-width: 100%;
          transition: color 0.2s ease;
        }
        
        @media (min-width: 1024px) {
          .contact-service-name {
            font-size: 13px;
            line-height: 1.4;
          }
        }
        
        .contact-service-card.selected .contact-service-name {
          color: #B91C1C;
        }
        
        /* ===== CONTINUE BUTTON ===== */
        .contact-continue-container {
          width: 100%;
          max-width: 600px;
          margin: 24px auto 0;
        }
        
        @media (min-width: 1024px) {
          .contact-continue-container {
            margin-top: 32px;
          }
        }
        
        .contact-continue-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 24px;
          font-size: 16px;
          font-weight: 700;
          color: white;
          background: #7C0F0F;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        
        @media (min-width: 1024px) {
          .contact-continue-btn {
            padding: 18px 32px;
            font-size: 18px;
          }
        }
        
        .contact-continue-btn:not(:disabled):hover {
          background: #6B0D0D;
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.3);
        }
        
        .contact-continue-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #5A0A0A;
        }
        
        .contact-continue-btn svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
          transition: transform 0.2s ease;
        }
        
        .contact-continue-btn:not(:disabled):hover svg:last-child {
          transform: translateX(4px);
        }
        
        /* ===== TRUST BADGES ===== */
        .contact-trust-badges {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
          margin-top: 32px;
        }
        
        @media (min-width: 1024px) {
          .contact-trust-badges {
            gap: 20px;
            margin-top: 40px;
          }
        }
        
        .contact-trust-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.15);
          backdrop-filter: blur(8px);
          border-radius: 20px;
          white-space: nowrap;
        }
        
        @media (min-width: 1024px) {
          .contact-trust-badge {
            padding: 10px 18px;
          }
        }
        
        .contact-trust-badge svg {
          width: 16px;
          height: 16px;
          color: #4ade80;
          flex-shrink: 0;
        }
        
        @media (min-width: 1024px) {
          .contact-trust-badge svg {
            width: 18px;
            height: 18px;
          }
        }
        
        .contact-trust-badge span {
          font-size: 12px;
          color: white;
          font-weight: 500;
        }
        
        @media (min-width: 1024px) {
          .contact-trust-badge span {
            font-size: 13px;
          }
        }
      `}</style>

      <section className="contact-section" id="contacto">
        {/* Background layers */}
        <div className="contact-bg" />
        <div className="contact-bg-overlay" />
        <div className="contact-bg-grid" />

        {/* Animated particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1.5 h-1.5 bg-white/20 rounded-full"
              style={{
                left: `${15 + i * 14}%`,
                top: `${20 + i * 12}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.1, 0.25, 0.1],
              }}
              transition={{
                duration: 4 + i,
                repeat: Infinity,
                delay: i * 0.5,
              }}
            />
          ))}
        </div>

        <div className="contact-container">
          {/* Chapter Indicator */}
          <div className="flex justify-center mb-6">
            <ChapterIndicator act={t.cta.act} subtitle={t.cta.actSubtitle} onDark />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {/* Header */}
            <div className="contact-header">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="contact-chapter-badge"
              >
                <span className="pulse-dot" />
                {t.cta.urgencyBadge}
              </motion.div>

              <h2 className="contact-title">
                {t.cta.title} <span>{t.cta.titleAccent}</span>
              </h2>

              <p className="contact-subtitle">{t.cta.subtitle}</p>
            </div>

            {/* Form Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="contact-form-card"
            >
              {/* Stepper */}
              <div className="contact-stepper">
                <div className="contact-step active">
                  <span className="contact-step-circle active">1</span>
                  <span className="contact-step-label">{t.cta.step1Label}</span>
                </div>
                <ArrowRight className="contact-step-arrow" />
                <div className="contact-step">
                  <span className="contact-step-circle inactive">2</span>
                  <span className="contact-step-label">{t.cta.step2Label}</span>
                </div>
                <ArrowRight className="contact-step-arrow" />
                <div className="contact-step">
                  <span className="contact-step-circle inactive">3</span>
                  <span className="contact-step-label">{t.cta.step3Label}</span>
                </div>
              </div>

              <h3 className="contact-step-title">{t.cta.stepTitle}</h3>

              {/* Services Grid */}
              <div className="contact-services-grid">
                {services.map((service) => {
                  const Icon = service.icon;
                  const isSelected = selectedService === service.id;

                  return (
                    <motion.div
                      key={service.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`Seleccionar servicio: ${service.name}`}
                      aria-pressed={isSelected}
                      onClick={() => handleServiceSelect(service.id)}
                      onKeyDown={(e) => handleKeyDown(e, service.id)}
                      whileTap={{ scale: 0.98 }}
                      className={`contact-service-card ${isSelected ? "selected" : ""}`}
                    >
                      <Icon className="contact-service-icon" aria-hidden="true" />
                      <span className="contact-service-name">{service.name}</span>
                    </motion.div>
                  );
                })}
              </div>

              {/* Continue Button */}
              <div className="contact-continue-container">
                <button
                  onClick={handleContinue}
                  disabled={!selectedService}
                  aria-disabled={!selectedService}
                  aria-label={selectedService ? "Continuar al paso 2" : "Selecciona un servicio primero"}
                  className="contact-continue-btn"
                >
                  <Calendar aria-hidden="true" />
                  {t.cta.continueBtn}
                  <ArrowRight aria-hidden="true" />
                </button>
              </div>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="contact-trust-badges"
            >
              {trustItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  className="contact-trust-badge"
                >
                  <item.icon aria-hidden="true" />
                  <span>{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default CinematicFinalCTA;
